import { textToBytes } from '../encoding';

export type ByteSearchMode = 'hex' | 'ascii' | 'number';
export type NumericWidth = 1 | 2 | 4;
export type ByteOrder = 'little' | 'big';

export interface BytePattern {
  values: Uint8Array;
  masks: Uint8Array;
}

export interface NumericSearchOptions {
  width: NumericWidth;
  byteOrder: ByteOrder;
}

export function compileHexPattern(input: string): BytePattern {
  const trimmed = input.trim();
  const compact = trimmed.replace(/^0x/i, '');
  const tokens =
    !/[\s,]/.test(trimmed) && compact.length % 2 === 0 && /^[0-9a-fA-F?]+$/.test(compact)
      ? (compact.match(/.{2}/g) ?? [])
      : trimmed.split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) {
    throw new Error('Enter one or more hex bytes');
  }

  const values = new Uint8Array(tokens.length);
  const masks = new Uint8Array(tokens.length);
  tokens.forEach((rawToken, index) => {
    const token = rawToken.replace(/^0x/i, '');
    if (!/^[0-9a-fA-F?]{2}$/.test(token)) {
      throw new Error(`Invalid hex search token: ${rawToken}`);
    }
    const highKnown = token[0] !== '?';
    const lowKnown = token[1] !== '?';
    values[index] =
      (highKnown ? Number.parseInt(token[0], 16) << 4 : 0) |
      (lowKnown ? Number.parseInt(token[1], 16) : 0);
    masks[index] = (highKnown ? 0xf0 : 0) | (lowKnown ? 0x0f : 0);
  });

  return { values, masks };
}

export function compileNumericPattern(input: string, options: NumericSearchOptions): BytePattern {
  const trimmed = input.trim();
  const value = trimmed.toLowerCase().startsWith('0x')
    ? Number.parseInt(trimmed.slice(2), 16)
    : Number(trimmed);
  const max = 2 ** (options.width * 8) - 1;
  if (!Number.isInteger(value) || value < 0 || value > max) {
    throw new Error(`Number must be an integer from 0 to ${max}`);
  }

  const buffer = new ArrayBuffer(options.width);
  const view = new DataView(buffer);
  if (options.width === 1) view.setUint8(0, value);
  if (options.width === 2) view.setUint16(0, value, options.byteOrder === 'little');
  if (options.width === 4) view.setUint32(0, value, options.byteOrder === 'little');
  return {
    values: new Uint8Array(buffer),
    masks: new Uint8Array(options.width).fill(0xff),
  };
}

export function compileBytePattern(
  input: string,
  mode: ByteSearchMode,
  numeric: NumericSearchOptions = { width: 1, byteOrder: 'little' },
): BytePattern {
  if (mode === 'hex') return compileHexPattern(input);
  if (mode === 'number') return compileNumericPattern(input, numeric);
  const values = textToBytes(input);
  if (values.length === 0) throw new Error('Enter text to search for');
  return { values, masks: new Uint8Array(values.length).fill(0xff) };
}

export function findByteMatches(bytes: Uint8Array, pattern: BytePattern): number[] {
  const matches: number[] = [];
  if (pattern.values.length === 0 || pattern.values.length > bytes.length) return matches;

  outer: for (let offset = 0; offset <= bytes.length - pattern.values.length; offset += 1) {
    for (let index = 0; index < pattern.values.length; index += 1) {
      if ((bytes[offset + index] & pattern.masks[index]) !== pattern.values[index]) {
        continue outer;
      }
    }
    matches.push(offset);
  }
  return matches;
}

export function parseByteOffset(input: string, byteLength: number): number {
  const trimmed = input.trim();
  const value = trimmed.toLowerCase().startsWith('0x')
    ? Number.parseInt(trimmed.slice(2), 16)
    : Number(trimmed);
  if (!Number.isSafeInteger(value) || value < 0 || value >= byteLength) {
    throw new Error(`Offset must be between 0 and ${Math.max(0, byteLength - 1)}`);
  }
  return value;
}
